/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react' // <-- ADICIONE useRef
import AutoCompleteInput from '../AutoCompleteInput'
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import StyledTextField from '../shared/StyledTextField'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ListboxComponent } from '../VirtualizedAutocomplete'
import { buildContextProps } from '../../utils/objectUtils'

const FieldWithSelectOrAutoComplete = ({
  field,
  formData,
  formErrors,
  onFieldChange,
  getOptions,
  getSelectedValue,
  getSelectMeta,
}) => {
  const [category, key] = field.id.includes('.')
    ? field.id.split('.')
    : [null, field.id]

  const [isModalOpen, setModalOpen] = useState(false)
  const [refetchFn, setRefetchFn] = useState(null)

  const options = getOptions(field.id) ?? []

  const selectMeta = getSelectMeta?.(field.id) || {}
  const {
    hasNextPage = false,
    isFetchingNextPage = false,
    isLoading = false,
    isFetching = false,
    handleMenuScrollToBottom,
    handleSearch,
    onFocus,
  } = selectMeta

  const isAnyLoading = isLoading || isFetching || isFetchingNextPage

  const handleOpenModal = () => setModalOpen(true)
  const handleCloseModal = () => setModalOpen(false)

  const handleRefetch = useCallback(() => {
    if (refetchFn) refetchFn()
  }, [refetchFn])

  const handleSetRefetch = useCallback((cb) => setRefetchFn(() => cb), [])

  const extractId = (item) => {
    if (item == null) return null
    if (typeof item === 'object' && 'value' in item) return item.value
    if (typeof item === 'object' && 'id' in item) return item.id
    return item
  }

  const memorizedOnChange = useCallback(
    (sel) => {
      if (!field.entity) {
        onFieldChange(field.id, field.isMulti ? sel || [] : sel)
        return
      }

      if (field.isMulti) {
        const values = Array.isArray(sel)
          ? sel.map(extractId).filter(Boolean)
          : []
        onFieldChange(field.id, values)
      } else {
        onFieldChange(field.id, extractId(sel))
      }
    },
    [onFieldChange, field.id, field.isMulti, field.entity]
  )

  const currentFieldError = useMemo(
    () => formErrors?.[category]?.[key] || formErrors?.[field.id] || '',
    [formErrors, category, key, field.id]
  )

  const currentValueForSelect = useMemo(() => {
    const raw = getSelectedValue(field.id)

    const sanitize = (item) => {
      if (
        typeof item === 'object' &&
        item !== null &&
        'value' in item &&
        'label' in item
      ) {
        return item
      }
      const matched = options.find((opt) => opt.value === item)
      return (
        matched || {
          value: item ?? 'missing-value',
          label: `missing-label (ID: ${item})`,
        }
      )
    }

    if (field.isMulti) {
      if (!Array.isArray(raw)) return []
      return raw.map(sanitize)
    }

    return raw ? sanitize(raw) : null
  }, [field.id, field.isMulti, getSelectedValue, options])

  const memoizedExcludeIds = useMemo(
    () => formData.exclude_ids?.[field.id] || [],
    [formData.exclude_ids, field.id]
  )

  const memoizedFilters = useMemo(
    () => formData.filters?.[field.id] || {},
    [formData.filters, field.id]
  )

  const hasModalSelect = field.options?.some(
    (opt) => opt.type === 'modal-select'
  )

  const modalContextProps = useMemo(() => {
    const modalConfig = field.options?.find((opt) => opt.type === 'modal')
    if (!modalConfig?.contextProps) return {}
    return buildContextProps(formData, modalConfig.contextProps)
  }, [formData, field.options])

  const modalButtonConfig = field.options?.find((opt) => opt.type === 'modal')
  const shouldUseVirtualization = options.length > 50

  const renderModalSelectButton = () => (
    <Button
      variant={currentFieldError ? 'outlined' : 'contained'}
      color={currentFieldError ? 'error' : 'primary'}
      className="btn btn-blue-light fw-semibold"
      fullWidth={field.fullWidth}
      onClick={handleOpenModal}
      style={{ justifyContent: 'flex-start' }}>
      {field.placeholder || `Selecionar ${field.label}`}
    </Button>
  )

  const [inputValue, setInputValue] = useState('')

  // NOVO: Ref para saber se o campo ANTERIORMENTE tinha um valor selecionado
  const prevHasValueRef = useRef(false)

  // CORREÇÃO: Só limpa o input se um valor existente foi REMOVIDO (ex: clique no 'X')
  // Se o campo está vazio desde o início (busca), NÃO apaga o que o usuário digita.
  useEffect(() => {
    const currentHasValue = !!(
      currentValueForSelect &&
      (!Array.isArray(currentValueForSelect) ||
        currentValueForSelect.length > 0)
    )

    if (prevHasValueRef.current && !currentHasValue) {
      setInputValue('')
    }

    prevHasValueRef.current = currentHasValue
  }, [currentValueForSelect])

  const renderSelectInput = () => (
    <Box display="flex" alignItems="center" gap={1}>
      <Box flexGrow={1}>
        <Autocomplete
          key={currentValueForSelect?.value || 'empty'}
          multiple={field.isMulti}
          disableCloseOnSelect={!!field.isMulti} 
          disabled={field.disabled}
          options={options ?? []}
          inputValue={inputValue}
          value={currentValueForSelect}
          onChange={(e, val) => memorizedOnChange(val)}
          disableClearable={!field.isClearable}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          getOptionLabel={(option) =>
            option && typeof option === 'object' && 'label' in option
              ? String(option.label)
              : ''
          }
          noOptionsText={`Nenhuma opção encontrada para ${field.label}`}
          loading={isAnyLoading}
          ListboxComponent={
            shouldUseVirtualization ? ListboxComponent : undefined
          }
          ListboxProps={{
            onScroll: (event) => {
              const listboxNode = event.currentTarget
              if (shouldUseVirtualization) {
                const { scrollTop, scrollHeight, clientHeight } = listboxNode
                if (
                  scrollTop + clientHeight >= scrollHeight * 0.6 &&
                  hasNextPage &&
                  handleMenuScrollToBottom
                ) {
                  handleMenuScrollToBottom()
                }
              } else {
                const position =
                  listboxNode.scrollTop + listboxNode.clientHeight
                if (
                  position >= listboxNode.scrollHeight * 0.9 &&
                  hasNextPage &&
                  handleMenuScrollToBottom
                ) {
                  handleMenuScrollToBottom()
                }
              }
            },
          }}
          onInputChange={(event, value, reason) => {
            setInputValue(value)
            if (reason === 'input' && handleSearch) {
              handleSearch(value)
            }
          }}
          filterOptions={
            field.entity
              ? (x) => x
              : (options, { inputValue }) =>
                  options.filter((opt) =>
                    opt.label?.toLowerCase().includes(inputValue.toLowerCase())
                  )
          }
          onOpen={(e) => {
            if (onFocus) onFocus()
          }}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              error={!!currentFieldError}
              helperText={currentFieldError || field.helperText}
              size="small"
              label={field.label}
              placeholder={field.placeholder}
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                ...(!field.isMulti && {
                  startAdornment: field.icon ? (
                    <InputAdornment position="end">
                      <FontAwesomeIcon icon={field.icon} size="sm" />
                    </InputAdornment>
                  ) : null,
                }),
                endAdornment: (
                  <>
                    {isAnyLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          )}
          clearOnBlur={false}
        />
      </Box>

      {modalButtonConfig && (
        <Tooltip title={modalButtonConfig.tooltip || 'Cadastrar'}>
          <IconButton
            onClick={handleOpenModal}
            disabled={field.disabled}
            size="small"
            sx={{
              border: '1px solid #ccc',
              borderRadius: '6px',
              height: '40px',
              width: '40px',
            }}>
            <FontAwesomeIcon icon={modalButtonConfig.icon} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )

  return (
    <Box mb="4px">
      {hasModalSelect ? (
        renderModalSelectButton()
      ) : field.entity ? (
        <AutoCompleteInput
          entity={field.entity}
          column={field.column}
          columnLabel={field.columnLabel}
          columnDetails={field.columnDetails}
          isMulti={field.isMulti}
          isClearable={field.isClearable}
          placeholder={field.placeholder}
          disabled={field.disabled}
          onChange={memorizedOnChange}
          callBack={handleSetRefetch}
          value={currentValueForSelect}
          exclude_ids={memoizedExcludeIds}
          filters={memoizedFilters}
        />
      ) : (
        renderSelectInput()
      )}

      {field.options?.map(
        (opt) =>
          opt.type === 'modal-select' && (
            <opt.children
              key={opt.type}
              open={isModalOpen}
              onClose={handleCloseModal}
              onSave={(selectedItems) => {
                memorizedOnChange(selectedItems)
                setModalOpen(false)
              }}
              entity={field.entity}
              column={field.column}
              columnLabel={field.columnLabel}
              isMulti={field.isMulti}
              selectedItems={getSelectedValue(field.id)}
            />
          )
      )}

      {modalButtonConfig && (
        <modalButtonConfig.children
          open={isModalOpen}
          onClose={handleCloseModal}
          callBack={handleRefetch}
          {...modalContextProps}
        />
      )}
    </Box>
  )
}

export default React.memo(FieldWithSelectOrAutoComplete)
